import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import AIChatWidget from '@/components/AIChatWidget';
import {
  Clock,
  MapPin,
  Shield,
  Smartphone,
  Bell,
  FileText,
  Users,
  Zap,
  CheckCircle,
  XCircle,
  DollarSign,
  TrendingUp,
  Brain,
  Rocket,
  Timer,
  AlertTriangle,
  Calculator,
  ChevronRight,
  ArrowRight,
  Coffee,
  Building,
  Star,
  Menu,
  X,
  ScanFace,
  ArrowLeftRight,
  Calendar,
  MessageCircle,
  Download
} from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [employees, setEmployees] = useState(10);
  const [hourlyWage, setHourlyWage] = useState(20);

  // Calculate savings
  const calculateSavings = (minutesPerDay) => {
    const dailyCost = (minutesPerDay / 60) * hourlyWage * employees;
    const weeklyCost = dailyCost * 5;
    const yearlyCost = weeklyCost * 52;
    return { weekly: weeklyCost.toFixed(2), yearly: yearlyCost.toFixed(0) };
  };

  const savings5 = calculateSavings(5);
  const savings10 = calculateSavings(10);
  const savings15 = calculateSavings(15);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">ClockIn Pro</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-indigo-600 transition-colors">Features</a>
              <a href="#savings" className="text-slate-600 hover:text-indigo-600 transition-colors">Savings Calculator</a>
              <a href="#pricing" className="text-slate-600 hover:text-indigo-600 transition-colors">Pricing</a>
              <a href="#about" className="text-slate-600 hover:text-indigo-600 transition-colors">About</a>
              <Link to="/login">
                <Button variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 cursor-not-allowed opacity-90">
                  Coming Soon
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-4">
            <a href="#features" className="block text-slate-600 hover:text-indigo-600">Features</a>
            <a href="#savings" className="block text-slate-600 hover:text-indigo-600">Savings Calculator</a>
            <a href="#pricing" className="block text-slate-600 hover:text-indigo-600">Pricing</a>
            <a href="#about" className="block text-slate-600 hover:text-indigo-600">About</a>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full border-indigo-600 text-indigo-600">Sign In</Button>
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-indigo-100 text-indigo-700 hover:bg-indigo-100 px-4 py-1.5">
              <Brain className="w-4 h-4 mr-2" />
              AI-Powered Time Tracking
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Stop Chasing Paper,{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Start Growing Your Business
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Enterprise-grade time tracking built by AI, priced for small business. 
              Go from signup to your first clock-in in under 3 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg px-8 py-6 cursor-not-allowed opacity-90"
                disabled
              >
                Coming Soon
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="#features">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  See How It Works
                </Button>
              </a>
            </div>
            <p className="mt-6 text-sm text-slate-500">
              Sign-up opening soon. Explore features below.
            </p>
          </div>

          {/* Hero Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '3 min', label: 'Setup Time' },
              { value: '$14/mo', label: 'Starting At' },
              { value: '24/7', label: 'AI Support' },
              { value: '100%', label: 'Self-Service' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-indigo-600">{stat.value}</div>
                <div className="text-slate-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section - The Hidden Leak */}
      <section id="savings" className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-500/20 text-red-400 hover:bg-red-500/20 border-red-500/30">
              <AlertTriangle className="w-4 h-4 mr-2" />
              The Hidden Problem
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Is Your Payroll <span className="text-red-400">Leaking Money</span>?
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Most small businesses lose <span className="text-white font-semibold">1% to 5%</span> of their total gross payroll 
              to "time theft" and manual entry errors. It doesn't sound like much until you see the math.
            </p>
          </div>

          {/* Savings Calculator */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-indigo-400" />
                  Savings Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-slate-300">Number of Employees</Label>
                  <Input 
                    type="number" 
                    value={employees}
                    onChange={(e) => setEmployees(Math.max(1, parseInt(e.target.value) || 1))}
                    className="mt-2 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Average Hourly Wage ($)</Label>
                  <Input 
                    type="number" 
                    value={hourlyWage}
                    onChange={(e) => setHourlyWage(Math.max(1, parseInt(e.target.value) || 1))}
                    className="mt-2 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <Separator className="bg-slate-700" />
                <div className="text-center">
                  <p className="text-slate-400 mb-2">Your potential yearly savings:</p>
                  <p className="text-4xl font-bold text-green-400">${savings5.yearly}</p>
                  <p className="text-sm text-slate-500 mt-1">by eliminating just 5 minutes of time leakage per day</p>
                </div>
              </CardContent>
            </Card>

            <div>
              <h3 className="text-2xl font-bold mb-6">The Cost of "Just 5 Minutes"</h3>
              <p className="text-slate-400 mb-6">
                If you have <span className="text-white font-semibold">{employees} employees</span> earning <span className="text-white font-semibold">${hourlyWage}/hour</span>, 
                and each one "rounds up" or clocks in early every day:
              </p>
              
              <div className="overflow-hidden rounded-xl border border-slate-700">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-slate-300">Time Leak</th>
                      <th className="px-4 py-3 text-right text-slate-300">Weekly Cost</th>
                      <th className="px-4 py-3 text-right text-slate-300">Yearly Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr className="bg-slate-800/50">
                      <td className="px-4 py-3 text-slate-300">5 mins/day</td>
                      <td className="px-4 py-3 text-right text-yellow-400">${savings5.weekly}</td>
                      <td className="px-4 py-3 text-right text-red-400 font-semibold">${savings5.yearly}</td>
                    </tr>
                    <tr className="bg-slate-800/50">
                      <td className="px-4 py-3 text-slate-300">10 mins/day</td>
                      <td className="px-4 py-3 text-right text-yellow-400">${savings10.weekly}</td>
                      <td className="px-4 py-3 text-right text-red-400 font-semibold">${savings10.yearly}</td>
                    </tr>
                    <tr className="bg-slate-800/50">
                      <td className="px-4 py-3 text-slate-300">15 mins/day</td>
                      <td className="px-4 py-3 text-right text-yellow-400">${savings15.weekly}</td>
                      <td className="px-4 py-3 text-right text-red-400 font-semibold">${savings15.yearly}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
                <p className="text-indigo-300">
                  <strong className="text-indigo-200">The Point:</strong> ClockIn Pro doesn't just track time—it pays for itself 
                  by stopping the "silent" costs that manual spreadsheets can't catch.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
              <Zap className="w-4 h-4 mr-2" />
              Powerful Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Turn Your Minutes into Milestones
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Effective time management isn't about "watching" your employees—it's about empowering them. 
              When your team knows their time is valued and accurately recorded, morale goes up and disputes go down.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <MapPin className="w-6 h-6" />,
                title: 'GPS Geofencing',
                description: 'Ensure employees are actually at the workplace when they hit "Start." GPS-verified clock-ins eliminate buddy punching.',
                color: 'bg-green-100 text-green-600'
              },
              {
                icon: <Smartphone className="w-6 h-6" />,
                title: 'PWA Mobile App',
                description: 'Install directly from your browser - no app store needed. Works offline and sends push notifications like a native app.',
                color: 'bg-blue-100 text-blue-600'
              },
              {
                icon: <Bell className="w-6 h-6" />,
                title: 'Smart Notifications',
                description: 'Push notifications for shift reminders, late alerts, clock-out reminders, and shift swap requests. Never miss an update.',
                color: 'bg-yellow-100 text-yellow-600'
              },
              {
                icon: <Calendar className="w-6 h-6" />,
                title: 'Roster Builder',
                description: 'Weekly & monthly views with drag-and-drop scheduling. Today highlighted, shift swaps with approval workflow, and incomplete roster alerts.',
                color: 'bg-purple-100 text-purple-600'
              },
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'One-Click Payroll',
                description: 'Automated payroll reminders on your schedule. Export CSV for any payroll system. Reduce a 4-hour task to 10 minutes.',
                color: 'bg-indigo-100 text-indigo-600'
              },
              {
                icon: <MessageCircle className="w-6 h-6" />,
                title: 'AI Support & Tickets',
                description: '24/7 AI chatbot that actually helps. Complex issues automatically escalate to our ticket system for human review.',
                color: 'bg-pink-100 text-pink-600'
              }
            ].map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-300">
                <CardContent className="pt-6">
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Features */}
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                    <ArrowLeftRight className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Shift Swap Workflow</h3>
                    <p className="text-slate-600">
                      Employees can request to swap shifts with coworkers. Double approval workflow ensures 
                      both the coworker and manager agree before the swap is finalized.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Overtime Tracking</h3>
                    <p className="text-slate-600">
                      Configure weekly or fortnightly periods. Get real-time progress bars, projected overtime 
                      calculations, and automatic alerts before employees hit overtime.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* More Features Row */}
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <Card className="border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
                  <Coffee className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Break Analytics</h3>
                <p className="text-sm text-slate-600">Track breakfast and lunch breaks per employee with configurable durations and warnings.</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center mb-3">
                  <Download className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Install to Home Screen</h3>
                <p className="text-sm text-slate-600">One-tap installation on mobile. No app store, no downloads. Just instant access from your home screen.</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center mb-3">
                  <Shield className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Compliance Ready</h3>
                <p className="text-sm text-slate-600">Unalterable digital logs, archived employee records, and compliance-ready exports.</p>
              </CardContent>
            </Card>
          </div>

          {/* Facial Recognition Feature Card */}
          <Card className="mt-8 bg-gradient-to-br from-slate-900 to-indigo-900 border-indigo-500/30 text-white overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <ScanFace className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold">Facial Recognition</h3>
                    <Badge className="bg-indigo-500/30 text-indigo-200 border-indigo-400/30">Optional</Badge>
                  </div>
                  <p className="text-slate-300 text-lg mb-4">
                    Take time theft prevention to the next level with browser-based facial recognition. 
                    No special hardware required—works with any device camera.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-indigo-200">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span>Instant Verification</span>
                    </div>
                    <div className="flex items-center gap-2 text-indigo-200">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span>Liveness Detection</span>
                    </div>
                    <div className="flex items-center gap-2 text-indigo-200">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span>Privacy-First Design</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* AI Advantage Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-500/20 text-purple-300 hover:bg-purple-500/20 border-purple-500/30">
              <Brain className="w-4 h-4 mr-2" />
              The AI Advantage
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Cutting-Edge Tech. <span className="text-purple-400">Half the Cost.</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Most time-tracking companies charge high fees because they have massive overhead: armies of support staff, 
              manual data entry teams, and outdated servers. We've replaced the "middleman" with proprietary AI.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <DollarSign className="w-8 h-8" />,
                title: 'Lower Fees',
                description: 'Our AI-built platform means no heavy overhead. Enterprise tools at a "mom-and-pop" price.'
              },
              {
                icon: <Rocket className="w-8 h-8" />,
                title: 'Zero-Friction Setup',
                description: 'No "onboarding calls" or 24-hour waits. Go from sign-up to first clock-in in under 3 minutes.'
              },
              {
                icon: <Brain className="w-8 h-8" />,
                title: 'Smart Detection',
                description: 'AI scans for anomalies like forgotten clock-outs or overlapping shifts before they become costly errors.'
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: 'Always Evolving',
                description: 'Continuous AI optimization means your platform gets smarter, faster, and more secure every day.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Self-Service Section */}
          <div className="mt-16 bg-white/5 rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                Built for the Modern Owner: <span className="text-purple-400">No Salesmen, No Waiting</span>
              </h3>
              <p className="text-slate-300 mb-8">
                We believe small business owners are the most capable people on the planet. You don't need a 
                "dedicated representative" to hold your hand—you need a platform that just works.
              </p>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { title: 'Instant Customization', desc: 'Tailor departments, pay rates, and geofencing rules instantly' },
                  { title: '24/7 AI Assistance', desc: 'Generate reports or solve issues at 2 AM—our AI never sleeps' },
                  { title: 'Transparent Pricing', desc: 'No "Contact Sales for a Quote." Public, flat, and fair.' }
                ].map((item, index) => (
                  <div key={index} className="p-4 rounded-xl bg-white/5">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
                    <h4 className="font-semibold mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-slate-200 text-slate-700 hover:bg-slate-200">
              The Bottom Line
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              See the Difference
            </h2>
            <p className="text-xl text-slate-600">
              Traditional software vs. our AI-native approach
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-slate-600 font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center text-slate-500">The Legacy Guys</th>
                  <th className="px-6 py-4 text-center text-indigo-600 font-bold bg-indigo-50">ClockIn Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  { feature: 'Setup Time', legacy: '2-5 Business Days', us: 'Instant (3 Minutes)', usGood: true },
                  { feature: 'Support', legacy: 'Ticket Queues (24h+)', us: 'Instant AI Concierge', usGood: true },
                  { feature: 'Pricing', legacy: 'High Monthly + Per User', us: '$14/mo + $0.50/extra user', usGood: true },
                  { feature: 'Maintenance', legacy: 'Manual Updates', us: 'Continuous AI Optimization', usGood: true },
                  { feature: 'Onboarding', legacy: 'Required Demo Calls', us: 'Self-Service Portal', usGood: true },
                  { feature: 'Hidden Fees', legacy: 'Implementation, Training, Support', us: 'None - Ever', usGood: true }
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 text-center text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        {row.legacy}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-indigo-50/50">
                      <div className="flex items-center justify-center gap-2 text-indigo-600 font-medium">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {row.us}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-700 hover:bg-green-100">
              <DollarSign className="w-4 h-4 mr-2" />
              Simple Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              One Price. No Surprises.
            </h2>
            <p className="text-xl text-slate-600">
              Enterprise features at a price that makes sense for growing businesses
            </p>
          </div>

          <Card className="max-w-lg mx-auto border-2 border-indigo-600 shadow-2xl shadow-indigo-100">
            <CardContent className="p-8 text-center">
              <Badge className="mb-4 bg-indigo-100 text-indigo-700">Simple & Scalable</Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Business Plan</h3>
              <div className="my-6">
                <span className="text-5xl sm:text-6xl font-bold text-slate-900">$14</span>
                <span className="text-slate-600">/month</span>
              </div>
              <p className="text-slate-600 mb-2">Up to 15 employees included</p>
              <p className="text-indigo-600 font-medium mb-8">+ $0.50 per additional employee</p>
              
              <div className="text-left space-y-3 mb-8">
                {[
                  '15 employees included in base price',
                  'Unlimited time entries',
                  'GPS geofencing',
                  'PWA mobile app with push notifications',
                  'Facial recognition (optional)',
                  'Roster builder (weekly & monthly)',
                  'Shift swap workflows',
                  'Break tracking & analytics',
                  'Overtime tracking & alerts',
                  'Payroll reminders',
                  'AI support chatbot',
                  'Support ticket system',
                  'CSV report exports',
                  '24/7 availability',
                  'No setup fees'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-sm text-slate-600">
                <p className="font-medium text-slate-900 mb-2">Example Pricing:</p>
                <p>10 employees: <strong>$14/mo</strong></p>
                <p>20 employees: <strong>$16.50/mo</strong> ($14 + $2.50)</p>
                <p>50 employees: <strong>$31.50/mo</strong> ($14 + $17.50)</p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg py-6 cursor-not-allowed opacity-90"
                disabled
              >
                Coming Soon
              </Button>
              <p className="mt-4 text-sm text-slate-500">
                Sign-up opening soon!
              </p>
            </CardContent>
          </Card>

          <div className="mt-12 text-center">
            <p className="text-slate-600">
              <strong className="text-slate-900">Why so affordable?</strong> Because our AI-built platform has no 
              bloated overhead. We pass the savings directly to you.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/20 border-indigo-500/30">
              Our Mission
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why We're Different
            </h2>
          </div>

          <div className="prose prose-lg prose-invert mx-auto">
            <p className="text-slate-300 text-lg leading-relaxed">
              At ClockIn Pro, we believe that small business owners are the backbone of the economy—yet they are 
              often the most underserved by "Big Tech."
            </p>
            
            <p className="text-slate-300 text-lg leading-relaxed">
              For years, time-tracking software has followed the same tired blueprint: bloated features you don't use, 
              mandatory "onboarding calls" with salespeople, and high monthly fees to cover massive corporate overhead.
            </p>

            <p className="text-slate-300 text-lg leading-relaxed">
              <strong className="text-white">We decided it was time for a change.</strong>
            </p>

            <div className="my-8 p-6 bg-white/5 rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-indigo-400 mb-3">The AI-Native Advantage</h3>
              <p className="text-slate-300 mb-0">
                We started with a simple question: <em>Why does good software have to be so expensive?</em> The answer 
                was "Legacy Bloat." Traditional companies spend millions on manual support teams and outdated infrastructure. 
                We took a different path. By building a fully AI-developed, self-service platform, we eliminated the "middleman."
              </p>
            </div>

            <p className="text-slate-300 text-lg leading-relaxed">
              Because our platform is engineered by cutting-edge AI, it is leaner, faster, and more secure than traditional apps. 
              We don't have thousands of employees, so you don't have to pay for them.
            </p>

            <div className="my-8 p-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl border border-indigo-500/20">
              <h3 className="text-xl font-bold text-white mb-3">We're In Your Corner</h3>
              <p className="text-slate-300 mb-0">
                We aren't just a software company; we are a productivity partner. We know that every minute you spend chasing 
                a paper timesheet is a minute you aren't growing your business. Our goal is to give you back your time and 
                protect your profits.
              </p>
            </div>

            <p className="text-xl text-center font-semibold text-indigo-400 mt-8">
              Welcome to the next generation of work.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Stop the Payroll Leak?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join thousands of small businesses who've taken control of their time tracking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-indigo-600 hover:bg-indigo-50 text-lg px-8 py-6 cursor-not-allowed opacity-90"
              disabled
            >
              Coming Soon
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Link to="/login">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              >
                Existing User? Sign In
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-indigo-200 text-sm">
            Self-service signup coming soon. Be the first to know when we launch.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">ClockIn Pro</span>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="#about" className="hover:text-white transition-colors">About</a>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            </div>
            <p className="text-sm">
              &copy; {new Date().getFullYear()} ClockIn Pro. Built with AI.
            </p>
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <AIChatWidget context="landing" />
    </div>
  );
};

export default LandingPage;
